import React, {
  useCallback, useContext, useEffect, useMemo, useState
} from 'react';

import { useHistory } from 'react-router-dom';

import keycloakService from '../../../keycloak_service';

import { useClientContext } from '../ClientData/ClientData';
import useUserDataFetch from '../../../utils/hooks/use_user_data_for_ids';

export const LOGIN_STATE = {
  LOADING: 'loading',
  SIGNED_IN: 'signed in',
  SIGNED_OUT: 'signed out',
}

const UserDataContext = React.createContext({
  error: null,
  signedInState: LOGIN_STATE.LOADING,
  userData: null,
  users: [],
  usersLoading: false,
  usersError: null,
  isLoggingIn: false,
  isAdmin: false,
  setUsers: () => { },
  login: () => { },
  logout: () => { },
  refetchUsers: () => { },
});

export const UserDataProvider = ({
  children,
}) => {
  const { isAuthEnabled, isAdminDashboardEnabled } = useClientContext();

  // 'loading' | 'signed in' | 'signed out'
  const [signedInState, setSignedInState] = useState(LOGIN_STATE.LOADING);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAdmin, setAdmin] = useState(false);
  const history = useHistory();
  const { push: setRoute } = history;

  const {
    data: users,
    loading,
    error,
    refetch: refetchUsers,
  } = useUserDataFetch();

  const handleSignIn = useCallback(() => {
    if (isLoggingIn) {
      return;
    }
    setIsLoggingIn(true);

    keycloakService.keycloak.login().then((isAuthenticated) => {
      setSignedInState(isAuthenticated ? LOGIN_STATE.SIGNED_IN : LOGIN_STATE.SIGNED_OUT);
      setIsLoggingIn(false);
      if (isAuthenticated) {
        keycloakService.keycloak.loadUserProfile().then((profile) => {
          setUserData({ ...profile, id: keycloakService.keycloak.idTokenParsed?.sub });
          setAdmin(keycloakService.hasAdminPrivileges());

          // Route the admin user directly to the Admin Dashboard
          if (profile?.username === 'admin' && isAdminDashboardEnabled) {
            setRoute('/admin/users');
          }
        });
      }
    }).catch((e) => {
      setIsLoggingIn(false);
      setAuthError(e)
    });
  }, [isLoggingIn, isAdminDashboardEnabled, setRoute]);

  const handleRegistration = useCallback(() => {
    if (isRegistering) {
      return;
    }
    setIsRegistering(true);

    keycloakService.keycloak.register();
  }, [isRegistering]);

  const handleLogout = useCallback(() => {
    history.replace('/');
    setAdmin(false);
    keycloakService.keycloak.logout();
  }, [history]);

  useEffect(() => {
    if (!isAuthEnabled || window.hasInitiatedAuth) {
      return;
    }

    window.hasInitiatedAuth = true;
    keycloakService.checkSSO().then((isAuthenticated) => {
      setSignedInState(isAuthenticated ? LOGIN_STATE.SIGNED_IN : LOGIN_STATE.SIGNED_OUT);
      setIsLoggingIn(false);
      if (isAuthenticated) {
        keycloakService.keycloak.loadUserProfile().then((profile) => {
          setUserData({ ...profile, id: keycloakService.keycloak.idTokenParsed?.sub });
          setAdmin(keycloakService.hasAdminPrivileges());

          // Route the admin user directly to the Admin Dashboard
          if (profile?.username === 'admin') {
            setRoute('/admin/users');
          }
        });
      }
    }).catch((e) => {
      setIsLoggingIn(false);
      setAuthError(e)
    });
  }, [handleSignIn, isAuthEnabled, setRoute]);

  const userDataContextValue = useMemo(() => ({
    error: authError,
    signedInState,
    userData,
    isAdmin,
    isLoggingIn,
    isRegistering,
    users: users || [],
    usersLoading: loading,
    usersError: error,
    login: handleSignIn,
    logout: handleLogout,
    register: handleRegistration,
    refetchUsers,
  }), [
    authError,
    signedInState,
    userData,
    isAdmin,
    isLoggingIn,
    isRegistering,
    users,
    loading,
    error,
    handleSignIn,
    handleLogout,
    handleRegistration,
    refetchUsers,
  ])

  return (
    <UserDataContext.Provider
      value={userDataContextValue}
    >
      {children}
    </UserDataContext.Provider>
  )
}

export const useUserContext = () => useContext(UserDataContext);

export default useUserContext;
